
import throttle from 'lodash/throttle';
import catchify from 'catchify';
import get from 'lodash/get';
import { MAX_COMMENT_PAGE_SIZE } from '@/constants/global';
import Intersect from 'vue-intersect';
import PDPSearch from './PDPSearch.vue';
import PDPCommentTabs from './PDPCommentTabs.vue';
import PDPCommentModal from '../comment-modal/PDPCommentModal.vue';
import PDPQuestionModal from '../question/PDPQuestionModal.vue';
import { DiscussionType } from '../../../constants/discussions';

// defining this as a true constant outside of the component so that it is only built once.
const filterDict = {
  newest: '&sort=date&order=desc',
  oldest: '&sort=date&order=asc',
  latest: '&sort=date&order=desc', // Add filter to find children?
  helpful: '&sort=helpful',
  unapproved: '&moderator_filter=unapproved',
  unresolved: '&moderator_filter=unresolved',
  'rating-asc': '&sort=rating&order=asc',
  'rating-desc': '&sort=rating&order=desc',
  all: '',
};

export default {
  name: 'PDPDiscussion',
  components: {
    Intersect,
    PDPCommentTabs,
    PDPCommentModal,
    PDPQuestionModal,
    PDPSearch,
  },
  inject: {
    forDiscussionPage: {
      default: false,
    },
  },
  props: {
    deal: {
      type: Object,
      required: true,
    },
    filterComments: {
      type: Boolean,
    },
    user: {
      type: Object,
      required: true,
    },
    commentType: {
      type: String,
      default: DiscussionType.review,
    },
    discussion: {
      type: Object,
      default: null,
    },
  },
  // Fetch is called on server-side when rendering the route,
  // ~ and on client-side when navigating. No need to call it manually
  async fetch() {
    const { isModerator } = this.user || {};

    // always load the comment statuses for moderators since this is only done once.
    if (isModerator) {
      this.fetchCommentStatuses();
    }

    // never fetch for the discussion page. We are only rendering for a single discussion and
    // ~ should never need to load more/different items.
    if (this.forDiscussionPage) {
      return;
    }

    // if the component was updated to "enteredView" prior to fetch then load the results.
    if (!this.enteredView) {
      return;
    }

    // We fetch directly if SSR, and for CSR if User is Moderator
    if (typeof window === 'undefined' || isModerator) {
      await this.fetchComments(this.initialPage);
    } else {
      this.throttle(); // Throttle for regular users
    }
  },
  data() {
    // many of the settings change defaults based on whether we are rendering for the discussion
    // ~ page or not, so let's set proper defaults
    let enteredView = false;
    let isLoadingComments = true;
    const comments = [];

    // now we can update the values established above if we know we are rendering for the
    // ~ discussion page
    if (this.forDiscussionPage) {
      comments.push(this.discussion);
      isLoadingComments = false;
      enteredView = true;
    }

    return {
      enteredView,
      comments,
      isLoadingComments,
      commentLink: {},
      filterQuery: '',
      hasReviewed: this.user.hasReviewed,
      hasPurchased: this.user.hasPurchased,
      isChildComment: false,
      replyId: null,
      replyLevel: 0,
      replyIndices: [],
      searchCount: 0,
      searchTerm: '',
      showCreateReplyModal: false,
      showCreateItemModal: false,
      sortQuery: '',
      unapprovedComments: {},
      unresolvedComments: {},
      // used to determine if we should include "Load More"
      lastFetchCount: 0,
      nextPageToFetch: 1,
      // we want to track whether to show all replies and maintain it as filters/sorts change
      showAllChildren: true,
    };
  },
  computed: {
    linkHash() {
      return this.$route?.hash || '';
    },
    // Parse comment hash, LinkHash[0] => #
    linkHashIsValid() {
      if (!this.linkHash) {
        return false;
      }

      if (this.linkHash.length < 2 || ['#reviews', '#questions'].includes(this.linkHash)) {
        return false;
      }

      return ['q', 'r'].includes(this.linkHash[1]);
    },
    discussionPageLinkHashIsValid() {
      if (!this.linkHash || this.linkHash.length < 2) {
        return false;
      }

      return !!parseInt(this.linkHash.slice(1), 10); // true if linkHash[1:] is integer
    },
    isModeratorView() {
      return this.user.isAuthenticated && (this.user.isModerator || this.user.isStaff);
    },
    allowFlagging() {
      return get(this.deal, ['discussion', 'options', 'allow_flagging'], false);
    },
    searchEnabled() {
      // search is not enabled on the discussion page.
      return get(this.deal, ['discussion', 'search_is_active'], false) && !this.forDiscussionPage;
    },
    forQuestions() {
      return this.commentType === DiscussionType.question;
    },
    forReviews() {
      return this.commentType === DiscussionType.review;
    },
    commentTypeTitle() {
      return this.forQuestions ? 'Question' : 'Review';
    },
    commentTypeTitleLc() {
      return this.commentTypeTitle.toLowerCase();
    },
    getLoginMessageHtml() {
      const askOrLeave = this.forQuestions ? 'ask' : 'leave';
      const loginOrVerifyLink = !this.user.isAuthenticated ? '/accounts/login/' : '/account/email/';
      const loginOrVerifyText = !this.user.isAuthenticated ? 'login' : 'verify your email';

      return `Please <a href="${loginOrVerifyLink}">${loginOrVerifyText}</a> to
              ${askOrLeave} a ${this.commentTypeTitleLc}.`;
    },
    showLoginMessage() {
      return !this.user.isAuthenticated || (!this.user.isEmailVerified && !this.user.isStaff);
    },
    showCreateCommentModal() {
      if (this.showCreateReplyModal) {
        return true;
      }

      return this.forReviews && this.showCreateItemModal;
    },
    showCreateQuestionModal() {
      return this.forQuestions && this.showCreateItemModal;
    },
    commentCount() {
      if (this.searchTerm) {
        return this.searchCount;
      }

      if (this.forQuestions) {
        return get(this, ['deal', 'deal_comment', 'comment_count'], 0);
      }

      return get(this, ['deal', 'deal_review', 'review_count'], 0);
    },
    highlightComment() {
      return this.searchCount > 0 ? this.searchTerm : '';
    },

    forceShowAll() {
      // if we are already showing all children then let's consider that a force
      if (this.showAllChildren) {
        return true;
      }

      // if we have search results then that is also a force
      if (this.searchCount > 0) {
        return true;
      }

      // finally, if this is a direct link to a comment then force show all replies.
      return this.threadFilterIsApplied;
    },
    threadFilterIsApplied() {
      return !!this.commentLink?.id;
    },
    searchOrThreadFilterIsApplied() {
      return !!this.searchTerm || this.threadFilterIsApplied;
    },
    showLoadMoreCommentsButton() {
      // never include the load more button on the discussion page. All replies are included on the
      // ~ the page, and we never want to show more than one discussion. So not needed.
      if (this.forDiscussionPage) {
        return false;
      }

      // if we have not entered view yet then we always want to include the load more button
      // ~ so that search engines can fetch discussions.
      if (!this.enteredView) {
        return true;
      }

      if (this.isLoadingComments || this.searchOrThreadFilterIsApplied) {
        return false;
      }

      return this.lastFetchCount >= MAX_COMMENT_PAGE_SIZE;
    },
    hasItems() {
      return !!this.comments?.length;
    },
    showSearchTools() {
      // never include search tools on the discussion page.
      if (this.forDiscussionPage) {
        return false;
      }

      if (!this.searchEnabled) {
        return false;
      }

      return this.hasItems || !!this.searchTerm;
    },
    showMoreButtonUrl() {
      // we need to include a way to detect if we are rendering for this components, so lets
      // ~ include the short comment type and the next page.
      return `${this.deal.get_absolute_url}?pt=${this.shortCommentType}&p=${this.nextPageToFetch}`;
    },
    showMoreButtonText() {
      if (!this.enteredView) {
        return `Load ${this.commentTypeTitle}s`;
      }
      return `Load More ${this.commentTypeTitle}s`;
    },
  },
  async created() {
    this.shortCommentType = this.forQuestions ? 'q' : 'r';
    this.initialPage = 1;

    // URL fragments (hash) are only available on client-side so once
    // the component is created we need to force it to fetch the comments
    if (this.linkHashIsValid) {
      this.commentLink = {
        type: this.commentType,
        id: this.linkHash.slice(2),
      };

      // we need to flag this as true to allow fetch to actually get results.
      this.enteredView = true;
    }

    // let's try to handle loading a deeper page for search engine spiders.
    if (this.$route?.query?.pt === this.shortCommentType) {
      const routePage = parseInt(this.$route?.query?.p, 10);
      if (routePage) {
        this.initialPage = routePage;
        // we need to flag this as true to allow fetch to actually get results.
        this.enteredView = true;
      }
    }

    // force fetch immediately so we can scroll if hash has been found
    if (!this.forDiscussionPage && this.enteredView) {
      await this.fetchComments(this.initialPage);
    }
  },
  mounted() {
    if (this.forDiscussionPage && this.discussionPageLinkHashIsValid) {
      const linkedComment = document.getElementById(this.$route.hash.slice(1));

      if (linkedComment) {
        setTimeout(() => {
          // hack to allow smooth scroll to comment when navigating to discussion page from pdp
          window.scrollTo(0, 0);

          const top = window.scrollY + linkedComment.getBoundingClientRect().top;

          // taken from PDPScrollToElementMixin -- notes on why we do this way can be found there
          if ('scrollBehavior' in document.documentElement.style) {
            window.scrollTo({ top, behavior: 'smooth' });
          } else {
            window.scrollTo(0, top);
          }
        }, 1);
      }
    }
  },
  methods: {
    replyComment(data = {}) {
      const { id, positionIndices, level = 0 } = data;

      if (!id) {
        this.$toastr.e('Something went wrong');
        return;
      }

      this.replyId = id;
      this.replyIndices = positionIndices;
      this.replyLevel = level + 1;

      this.toggleReplyModal();
    },
    onClickFlag() {
      // TODO -- Setup endpoint to flag
    },
    onSearchSubmit(term) {
      this.searchTerm = term;
      this.fetchComments();
    },
    async onSubmitComment(data = {}) {
      let securityHash = this.deal.discussion.hash_question;
      let objectKey = this.deal.discussion.object_pk_question;

      if (this.forReviews) {
        securityHash = this.deal.discussion.hash_review;
        objectKey = this.deal.discussion.object_pk_review;
      }

      const {
        comment,
        notifyEmail,
        rating,
        title,
      } = data;

      const dataProps = {
        comment,
        content_type: `deals.${this.commentType}`,
        email: this.user.email,
        followup: notifyEmail,
        honeypot: '',
        name: this.user.username,
        object_pk: objectKey,
        rating: rating || 0,
        reply_to: this.replyId || 0,
        security_hash: securityHash,
        timestamp: this.deal.discussion.timestamp,
        title: title || '',
      };

      const dataKeys = Object.keys(dataProps);
      let formBody = [];

      for (let i = 0; i < dataKeys.length; i += 1) {
        const key = dataKeys[i];
        const encodedKey = encodeURIComponent(key);
        const encodedValue = encodeURIComponent(dataProps[key]);
        formBody.push(`${encodedKey}=${encodedValue}`);
      }

      formBody = formBody.join('&');

      // Matches django version headers
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-CSRFToken': this.$cookies.get('csrftoken'),
        'Accept-Encoding': 'gzip, deflate',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: '*/*',
      };

      await this.$axios.post(
        '/comments/api/discussion/',
        formBody,
        { headers },
      )
        .then((response) => {
          if (response && response.data && response.data.new_comment_id) {
            const {
              comment: respComment,
              new_comment_id: newId,
              rating: respRating,
              reply_to: replyId,
              timestamp,
              title: respTitle,
            } = response.data;

            const {
              avatar,
              dateJoined,
              dealsPurchased,
              hasBriefcase,
              hasPlus,
              id: userId,
              isEmailVerified,
              isModerator,
              username,
            } = this.user;

            let contentId = 84;
            let commentPrefix = 'q';

            if (this.forReviews) {
              contentId = 85;
              commentPrefix = 'c';
              this.hasReviewed = true;
            }

            const newComment = {
              allow_reply: false,
              approved: undefined,
              children: [],
              comment: respComment,
              id: newId,
              is_flagged: 0,
              is_removed: 0,
              level: this.replyLevel || 0,
              num_likes: 0,
              parent_id: replyId,
              permalink: `/comments/cr/${contentId}/${this.deal.discussion.object_pk}/#${commentPrefix}${newId}`,
              pinned: null,
              purchased: this.user.hasPurchased,
              rating: respRating,
              resolved: this.forReviews, // Reviews are auto-resolved
              submit_date: timestamp,
              title: respTitle,
              user: {
                avatar,
                comment_blacklist: false,
                comment_whitelist: false,
                date_joined: dateJoined,
                deals_purchased: dealsPurchased,
                email_verified: isEmailVerified,
                has_briefcase: hasBriefcase,
                has_plus: hasPlus,
                id: userId,
                username,
              },
              user_moderator: isModerator,
              user_url: '', // This needed?
            };

            const updatedComments = [...this.comments];

            // Add comment to correct position in tree
            // 1st index is child, 2nd index is grandchild
            if (this.replyIndices.length === 1) {
              const { children } = updatedComments[this.replyIndices[0]];

              if (!children) {
                updatedComments[this.replyIndices[0]].children = [];
              }

              updatedComments[this.replyIndices[0]].children.unshift(newComment);
            } else if (this.replyIndices.length === 2) {
              const {
                children,
              } = updatedComments[this.replyIndices[0]].children[this.replyIndices[1]];

              if (!children) {
                updatedComments[this.replyIndices[0]].children[this.replyIndices[1]].children = [];
              }

              updatedComments[this.replyIndices[0]].children[this.replyIndices[1]].children
                .unshift(newComment);
            } else {
              updatedComments.unshift(newComment);
            }

            this.comments = updatedComments;

            this.$toastr.s('Success');
          }
        })
        .catch(() => {
          this.$toastr.e('Something went wrong');
        });

      if (this.replyId) {
        this.replyId = null;
        this.replyIndices = [];
        this.replyLevel = 0;
      }

      this.toggleCreateItemModal();
    },
    toggleCreateItemModal() {
      if (this.showCreateReplyModal) {
        this.toggleReplyModal();
        return;
      }

      this.showCreateItemModal = !this.showCreateItemModal;
      this.updateScrollToTopVisibility();
    },
    toggleReplyModal() {
      this.showCreateReplyModal = !this.showCreateReplyModal;
      this.updateScrollToTopVisibility();
    },
    toggleShowAllChildren() {
      this.showAllChildren = !this.showAllChildren;
    },
    updateAction(data = {}) {
      const {
        commentUserId,
        id,
        type,
        value,
        done,
      } = data;

      switch (type) {
        case 'approved': {
          let action = '1';

          if (typeof value === 'undefined' || value === null) {
            action = 'null';
          } else if (!value) {
            action = '0';
          }

          this.$axios.get(`/comments/approve_comment_ajx/?comment_id=${id}&approve_action=${action}`)
            .then((response) => {
              const { success } = (response && response.data) || {};

              done({ type, value, error: !success });
            })
            .catch(() => {
              done({ type, value, error: true });
            });

          return;
        }
        case 'resolved': {
          const reqData = { xtdcomment_ptr_id: id, resolved: value };
          const headers = {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$cookies.get('csrftoken'),
          };

          this.$axios.put(
            `/discussions/api/resolved/${id}/`,
            reqData,
            { headers },
          )
            .then((response) => {
              const { xtdcomment_ptr_id: cId, resolved } = (response && response.data) || {};

              done({ type, value: resolved, error: cId < 0 });
            })
            .catch(() => {
              done({ type, value, error: true });
            });

          return;
        }

        case 'pinned': {
          // todo:LED-1136: We need to support pinning for questions eventually.
          if (this.forQuestions) {
            return;
          }

          const action = value ? 1 : 0;

          this.$axios.get(`/comments/pin_comment_ajx/?comment_id=${id}&pin_action=${action}`)
            .then((response) => {
              const { success } = (response && response.data) || {};

              done({ type, value, error: !success });
            })
            .catch(() => {
              done({ type, value, error: true });
            });

          return;
        }

        case 'blacklist':
        case 'whitelist': {
          const action = value ? 1 : 0;

          this.$axios.get(`/comments/${type}_author_ajx/?user_id=${commentUserId}&${type}_action=${action}`)
            .then((response) => {
              const { success } = (response && response.data) || {};
              const error = !success;

              done({ type, value, error });
            })
            .catch(() => {
              done({ type, value, error: true });
            });

          return;
        }

        case 'upvote': {
          const headers = {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$cookies.get('csrftoken'),
          };

          this.$axios.post(
            `/comments/like/${id}/`,
            {},
            { headers },
          )
            .then((response) => {
              const { success, upvote_count: upvoteCount } = (response && response.data) || {};

              done({ type, value: upvoteCount, error: !success });
            })
            .catch(() => {
              done({ type, value, error: true });
            });

          return;
        }

        default:
          break;
      }

      done({ type, value, error: true });
    },
    async enteringView() {
      // only fetch the results if we have not already "enteredView" through pt/p param URLs
      if (this.enteredView) {
        return;
      }

      this.enteredView = true;
      if (this.isModeratorView) {
        await this.fetchCommentStatuses();
      }

      // let's always load the first page when entering view.
      this.fetchComments(1);
    },
    async fetchMoreComments() {
      // pass default pageToFetch (1) unless there is not a search or thread filter applied
      const pageToFetch = this.searchOrThreadFilterIsApplied ? 1 : this.nextPageToFetch;

      if (this.isModeratorView) {
        await this.fetchCommentStatuses();
      }

      this.fetchComments(pageToFetch);
    },
    async fetchComments(pageToFetch = 1) {
      this.setLoading(true);

      // Break the cache for moderators so everything is always up to date
      const breakCache = (this.user.isModerator) ? Date.now() : '';

      const { id: threadId = '' } = this.commentLink || {};

      // Make current comments disappear if starting over
      if (pageToFetch <= 1) {
        this.comments = [];
      }

      let queryUrl = '';

      if (this.searchTerm) {
        queryUrl = `/api/v2/deals/search_comments/?query=${this.searchTerm}&comment_type=${this.commentType}&deal_id=${this.deal.id}&cache=${breakCache}&for_nuxt_pdp=True`;
      } else {
        queryUrl = `/discussions/api/listcomments/${this.deal.id}/?group=${this.commentType}${this.filterQuery + this.sortQuery}&thread_id=${threadId}&page=${pageToFetch}&cache=${breakCache}&for_nuxt_pdp=True`;
      }

      try {
        const response = await this.$axios.get(queryUrl);

        if (response && response.data && response.data.comments) {
          const comments = get(response, ['data', 'comments'], []);
          this.searchCount = comments.length;

          if (pageToFetch > 0) {
            this.comments = this.comments.concat(comments);

            // it is vital that this is set off of the comments parsed from the API
            this.lastFetchCount = comments.length;
            this.nextPageToFetch = pageToFetch + 1;
          } else {
            this.comments = comments;
          }

          this.setLoading();

          // Workaround set the enteredView again right after fetch. For some reason
          // the component data is restared to the initial state right after calling $axios
          // and even more weird is not being caught when using watcher
          // TODO: More debugging to know why this happens
          this.enteredView = true;
        }
      } catch (e) {
        this.setLoading();
        this.$toastr.e('Failed to load comments');
      }
    },
    async fetchCommentStatuses() { // Want to fetch these at the same time
      const responses = await Promise.all([
        catchify(this.$axios.get(`/discussions/api/unresolved/${this.deal.id}/`)),
        catchify(this.$axios.get(`/discussions/api/unapproved/${this.deal.id}/`)),
      ]);

      const [[, unresolvedResp], [, unapprovedResp]] = responses;

      if (unresolvedResp && unresolvedResp.data) {
        this.unresolvedComments = unresolvedResp.data;
      }

      if (unapprovedResp && unapprovedResp.data) {
        this.unapprovedComments = unapprovedResp.data;
      }
    },
    setLoading(status = false) {
      this.isLoadingComments = status;
    },
    async onClickRefresh(data) {
      const { resetHash = false } = data;

      if (resetHash) {
        this.commentLink = {};
      }

      this.$fetch();
    },
    onChangeFilter({ type, value }) {
      if (type === 'filter') {
        this.filterQuery = filterDict[value];
      } else if (type === 'sort') {
        this.sortQuery = filterDict[value];
      }

      this.fetchComments();
    },
    updateScrollToTopVisibility() {
      this.$store.dispatch(
        'scrollToTop/updateVisibility',
        { hideScrollToTop: this.showCreateItemModal || this.showCreateReplyModal },
      );
    },
    // this is pure insanity, but we need to have different throttles for reviews vs questions
    // ~ otherwise the request for questions that is rendered first on the page causes a throttle
    // ~ for loading questions.
    throttle() {
      if (this.forReviews) {
        this.throttleReviews();
      } else {
        this.throttleQuestions();
      }
    },
    /* eslint-disable func-names */
    throttleQuestions: throttle(function () { this.fetchComments(); }, 30000), // 30 sec throttle
    throttleReviews: throttle(function () { this.fetchComments(); }, 30000), // 30 sec throttle
    /* eslint-enable func-names */
  },
};
